/**
 * Shared country-code helpers for the CA/US dual-country model.
 *
 * The DB's `accounts.country` + `org_settings.country` columns are typed as
 * `string` in the generated Supabase types (since Postgres CHECK constraints
 * don't propagate to TS), but they are constrained to `'ca' | 'us'` at the
 * DB level via migration 20260419_country_foundation.sql. These helpers
 * narrow + map at the application boundary so consumers can rely on the
 * literal union without scattering casts.
 *
 * Single source of truth — imported by:
 * - src/lib/account.jsx (AccountContextValue.country)
 * - src/lib/branding.jsx (BrandingContextValue.country + localeForCountry wiring)
 * - src/components/AiChat.jsx (system-prompt locale derivation)
 */

export type Country = 'ca' | 'us';
export type CountryLocale = 'en-CA' | 'en-US';

/**
 * Narrow an untrusted country string (from DB, metadata, or API) to the
 * `'ca' | 'us'` literal union. Unknown values fall back to 'ca' to match
 * the DB DEFAULT and preserve existing CA-tenant behaviour.
 */
export function narrowCountry(value: string | null | undefined): Country {
  return value === 'us' ? 'us' : 'ca';
}

/**
 * Map a country code to its preferred BCP-47 locale for `Intl.DateTimeFormat`
 * and `.toLocaleDateString` calls. Narrows the input defensively so callers
 * can pass any string (e.g. directly from DB) without pre-narrowing.
 *
 * `fr-CA` is explicitly out of scope per the US-expansion plan (§13) —
 * tracked as a future localization plan.
 */
export function localeForCountry(value: string | null | undefined): CountryLocale {
  return narrowCountry(value) === 'us' ? 'en-US' : 'en-CA';
}
